/**
 * The pure page math behind the Career Explorer's PDF viewer. Deliberately free of pdf.js (and of Angular) so it loads in the
 * unit tests and never pulls the library into a bundle.
 */

/** The worker pdf.js runs its parsing in; `apps/MJExplorer/angular.json` copies it from `pdfjs-dist/build` into assets (not bundled). */
export const PDF_WORKER_SRC = 'assets/pdf.worker.min.mjs';

/** The CV page's width at 100% zoom, in CSS px (P6:1754 `760`, the `.cx-doc` width). */
export const CV_PAGE_WIDTH_PX = 760;

/** A bbox as the provider sends it: normalized 0-1 of the page, origin top-left (`docs/julius/AGU-Career-Explorer-API-2026-10-26.md:136`). */
export type PdfBox = readonly [x0: number, y0: number, x1: number, y1: number];

/** The rendered page's size in CSS px. */
export interface PageViewport {
    readonly width: number;
    readonly height: number;
}

export interface CssBox {
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
}

export interface PlacedBox {
    readonly page: number;
    readonly bbox: PdfBox;
}

const Clamp = (value: number, low: number, high: number): number => Math.min(high, Math.max(low, value));

/** A highlight's normalized bbox as an absolutely positioned CSS box on the rendered page, clamped so it never spills off the page. */
export function BoxCss(bbox: PdfBox, viewport: PageViewport): CssBox {
    const left = Clamp(Math.min(bbox[0], bbox[2]) * viewport.width, 0, viewport.width);
    const right = Clamp(Math.max(bbox[0], bbox[2]) * viewport.width, 0, viewport.width);
    const top = Clamp(Math.min(bbox[1], bbox[3]) * viewport.height, 0, viewport.height);
    const bottom = Clamp(Math.max(bbox[1], bbox[3]) * viewport.height, 0, viewport.height);
    return { left, top, width: right - left, height: bottom - top };
}

/** Whether two highlight lists are the same boxes by value (a new array of the same boxes must not re-place or re-scroll). */
export function SameHighlights(a: readonly PlacedBox[], b: readonly PlacedBox[]): boolean {
    return a.length === b.length && a.every((h, i) => h.page === b[i].page && h.bbox.every((v, j) => v === b[i].bbox[j]));
}

/** The zoom that fits the page in the viewer body, in 5% steps between 50% and 100% (P6:1754-1759: `(clientWidth - 44) / 760`). */
export function FitZoom(containerWidth: number, pageWidthPx: number = CV_PAGE_WIDTH_PX): number {
    return Clamp(Math.floor(((containerWidth - 44) / pageWidthPx) * 20) / 20, 0.5, 1);
}
